import { useEffect, useState } from 'react';
import { fetchLeague } from '../api';
import { LeagueChronicleRow, LeagueStats } from '../types';
import TicketPage from './TicketPage';   // 🫳 줍줍 성적·조합 발굴 후보(뉴단타 해체 07-16 — 통계탭 흡수)
import AlertLivePanel from './AlertLivePanel';   // 🛰️ 알림 성적 라이브(서버 채점을 실시간 창구로)

// 📊 통계(관리자 전용) — **1층 스크롤**(08-03 전면 재설계, 07-24의 최상위 탭 2개 폐지):
//   ① 🎓 승격 심사 → ② 📡 오늘 → ③ 🌊 복기(접힘)  ← 여기까지 AlertLivePanel
//   ④ 💤 옛 기능 기록 — 탭 절반을 먹던 자리에서 맨 아래 접힘으로 강등(대부분 중단 💤 상태라).
//   ④는 **펼칠 때만 마운트** → 자동 비교 fetch·TicketPage 15초 폴링이 그때만 돈다(옛 탭과 같은 절약).

const PLAYER_DESC: Record<string, { name: string; desc: string; tag?: string }> = {
  s1: { name: 'v1 고정 기준값', desc: '첫 조립판 — 프로그램·선물·체결 공격성·호가 4가지를 정해진 자로 잰다. 큰 날엔 바늘이 끝에 붙어버리는(포화) 약점이 판정에서 드러나 강등.' },
  s2: { name: 'v2 적응 기준값', desc: '같은 4가지 재료를 "오늘 평소 대비 몇 배나 큰 움직임인지"로 잰다 — 조용한 날엔 민감하게, 큰 날엔 둔감하게 자가 조절. 7/9 세대 판정 승자.', tag: '현재 채택' },
  s3: { name: 'v3 국면게이트', desc: 'v2 점수에 "오늘 장 성격"(갭업이 되팔리는 날, 큰손이 사도 밀리는 날 등)을 읽어 상방 신호를 깎는다. 옛 티켓 기능(중단됨)의 계산 방식으로는 현역.' },
  s4: { name: 'v4 앙상블', desc: 'v2와 v3의 평균 — 국면 반영이 도움이 되긴 하는데 v3처럼 세게 깎는 건 과한 게 아닐까?를 시험하는 중간판.' },
  s5: { name: 'v5 베이시스', desc: 'v2 + 선물·현물 온도차(베이시스)의 1분 변화. 실측 예측력 1위(56.6%) 재료를 정식 성분으로 승격시키는 시험.' },
  s6: { name: 'v6 6성분', desc: '4가지 재료에 미국 선물 + 대형주 무리 움직임까지 더한 확장판. 7/9 판정에서 기준선 언저리(52.1%)로 탈락 — 재역전 감시 중.' },
  s7: { name: 'v7 체결강도', desc: 'v2 + 선물 체결강도(사자·팔자 힘겨루기)의 1분 변화. 실측 예측력 2위(56.4%) 재료의 승격 시험.' },
  s8: { name: 'v8 순수모멘텀', desc: '지수의 1분 관성 그대로를 점수로 — 복잡한 조합이 "그냥 가던 방향" 하나를 정말 이기는지 확인하는 대조군.' },
};

const fmtPnl = (v: number | null | undefined) =>
  v == null ? '—' : `${v > 0 ? '+' : ''}${v}%`;
const pnlCls = (v: number | null | undefined) =>
  v == null ? 'text-dc-ink3' : v > 0 ? 'text-dc-up' : v < 0 ? 'text-dc-down' : 'text-dc-ink2';

function DayCard({ c, names }: { c: LeagueChronicleRow; names: Record<string, string> }) {
  return (
    <div className="rounded-xl border border-dc-hair bg-dc-bg1/60 px-3 py-2.5 space-y-2">
      {/* 헤더: 날짜 · 뛴 버전 · 판정 */}
      <div className="flex items-center gap-2 flex-wrap text-[13px]">
        <span className="font-mono font-bold text-dc-ink1">{c.day.slice(5).replace('-', '/')}</span>
        <span className="text-dc-ink3">뛴 버전</span>
        <span className="text-dc-point font-semibold" title={PLAYER_DESC[c.ran]?.desc}>{names[c.ran] ?? c.ran}</span>
        <span className="ml-auto">
          {c.changed
            ? <span className="text-[11.5px] px-2 py-0.5 rounded-full border border-dc-point/50 bg-dc-point/10 text-[#EBCA96]" title={c.reason ?? ''}>
                🔁 다음날부터 {names[c.to ?? ''] ?? c.to}{c.reason?.includes('롤백') ? ' (롤백)' : ''}
              </span>
            : <span className="text-[11.5px] px-2 py-0.5 rounded-full border border-dc-line1 text-dc-ink3">유지</span>}
        </span>
      </div>
      {c.changed && c.reason && <p className="text-[10.5px] text-dc-point/70 leading-snug">└ {c.reason}</p>}

      {/* 자동 비교 순위 — 세로 리스트(잘림 없음) */}
      {(c.board?.length ?? 0) > 0 ? (
        <div className="space-y-0.5">
          {(c.board ?? []).map((b, i) => (
            <div key={b.key} className={`flex items-center gap-2 text-[11.5px] ${b.champ ? 'text-dc-point font-semibold' : 'text-dc-ink2'}`}
              title={PLAYER_DESC[b.key]?.desc}>
              <span className="w-4 text-right font-mono text-dc-ink3/70">{i + 1}.</span>
              <span>{b.name}</span>
              {b.champ && <span className="text-[11.5px] px-1 rounded border border-dc-point/40">현재 채택</span>}
              <span className="ml-auto font-mono">{b.rate}% <span className="text-dc-ink3/70 font-normal">({b.n}회)</span></span>
            </div>
          ))}
          {c.baseline != null && (
            <div className="flex items-center gap-2 text-[11.5px] text-dc-ink3/70 border-t border-dc-hair pt-0.5 mt-1">
              <span className="w-4" /><span>기준선(한 방향 찍기)</span>
              <span className="ml-auto font-mono">{c.baseline}%</span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-[11.5px] text-dc-ink3/70">이날은 자동 비교 기록 이전이라 순위 없음{c.baseline != null ? ` (기준선 ${c.baseline}%)` : ''}</p>
      )}

      {/* 하단: 매매 제안서 · 알림 */}
      <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-[11.5px] text-dc-ink3 border-t border-dc-hair pt-1.5">
        <span>🎫
          {(['A', 'B', 'H'] as const).map((k) => {
            const t = c.tickets?.[k];
            return (
              <span key={k} className="ml-1.5">
                {k} {t && t.n > 0 ? <><b className="text-dc-ink2">{t.win}승{t.n - t.win > 0 ? `${t.n - t.win}패·무` : ''}</b> <b className={`font-mono ${pnlCls(t.sum_pnl)}`}>{fmtPnl(t.sum_pnl)}</b></> : '—'}
              </span>
            );
          })}
        </span>
        <span>🔔 알림 {c.alerts ? <>{c.alerts.total}건 · 승률 <b className="text-dc-ink2">{c.alerts.win_rate ?? '—'}%</b></> : '—'}</span>
      </div>
    </div>
  );
}

// 🗄️ 옛 기능 기록 — 전면 개편(07-20) 이전 기계들의 기록 보관소(엔진 자동 비교 연대기·중단 줍줍/옛 저격 기능·힘 세기 1등 계산 방식·
//   계산 방식 소개·자가발전·일자별 자동 비교). 탭 B가 열릴 때만 마운트 → 자동 비교 fetch·TicketPage 폴링이 그때만 돈다.
function LegacyRecords() {
  const [data, setData] = useState<LeagueStats | null>(null);
  const [err, setErr] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showTicket, setShowTicket] = useState(false);   // 💤 멈춘 매매 제안서·옛 저격 기능 성적 — 기본 접힘(펼칠 때만 마운트=폴링 재개)
  useEffect(() => {
    let alive = true;
    fetchLeague().then((d) => { if (alive) setData(d); }).catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, []);

  const ch = data?.champion;
  const chron = data?.chronicle ?? [];
  const streaks = data?.streaks ?? {};
  const names = data?.players ?? {};

  return (
    <div className="space-y-3">
      <div>
        <h2 className="text-[17.5px] font-bold text-dc-ink1">🏟️ 계산 방식 자동 비교 기록</h2>
        <p className="text-[11.5px] text-dc-ink3 leading-snug mt-0.5">
          매일 밤 시스템이 계산 방식 후보들을 같은 시험지로 채점하고, 이긴 판을 다음날 자동 채택해요(3일 연속 우위 + 안전판).
          여기는 그 <b className="text-dc-ink2">경기 기록부</b>.
        </p>
      </div>

      {/* 💤 줍줍·옛 저격 기능 성적(07-20 중단) — 접힌 상태로 강등, 펼칠 때만 마운트해 15초 폴링 재개(코드 보존·마운트만 조건부) */}
      <div className="rounded-xl border border-dc-hair bg-dc-bg1/40">
        <button onClick={() => setShowTicket((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2 text-left">
          <span className="text-[13px] font-semibold text-dc-ink2">💤 중단(07-20, 되돌릴 수 있음) — 옛 줍줍·저격 기능의 마지막 성적</span>
          <span className="text-[11.5px] text-dc-ink3/70">평소엔 폴링 멈춤 · 펼치면 다시 불러옴</span>
          <span className="ml-auto text-[11.5px] text-dc-ink3">{showTicket ? '▾ 접기' : '▸ 펼치기'}</span>
        </button>
        {showTicket && <TicketPage section="ledger" embedded />}
      </div>

      {/* 현재 1등 계산 방식 + 도전 현황 */}
      <div className="rounded-xl border border-dc-point/40 bg-dc-point/[0.05] px-3 py-2.5 space-y-1 text-[13px]">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-dc-point font-bold">🏆 지금 쓰는 계산 방식</span>
          <span className="text-dc-ink1 font-semibold" title={ch ? PLAYER_DESC[ch.key]?.desc : undefined}>{ch?.name ?? '—'}</span>
          {ch?.since && <span className="text-dc-ink3">{ch.since}부터</span>}
          <span className="text-[11.5px] px-1.5 py-px rounded-full border border-dc-line1 text-dc-ink2">
            {ch?.by === 'league' ? '자동 비교로 채택' : '수동 승격(세대 판정)'}
          </span>
        </div>
        {Object.keys(streaks).length > 0 && (
          <p className="text-[11.5px] text-dc-ink3">
            도전 중: {Object.entries(streaks).map(([k, v]) => `${names[k] ?? k} ${v}연승`).join(' · ')} — 3연승이면 자동 교체
          </p>
        )}
      </div>

      {/* 계산 방식 소개 — 각 버전이 뭔지(07-10 피드백) */}
      <div className="rounded-xl border border-dc-hair bg-dc-bg1/60">
        <button onClick={() => setShowDesc((v) => !v)}
          className="w-full flex items-center gap-2 px-3 py-2 text-[13px] text-dc-ink2 font-medium">
          🎽 계산 방식 소개 — 버전별로 뭐가 다른가
          <span className="ml-auto text-dc-ink3/70 text-[11.5px]">{showDesc ? '▾ 접기' : '▸ 펼치기'}</span>
        </button>
        {showDesc && (
          <div className="px-3 pb-2.5 space-y-1.5">
            {Object.entries(PLAYER_DESC).map(([k, p]) => (
              <div key={k} className="text-[11.5px] leading-snug">
                <span className={`font-semibold ${ch?.key === k ? 'text-dc-point' : 'text-dc-ink2'}`}>{p.name}</span>
                {ch?.key === k && <span className="ml-1 text-[11.5px] px-1 rounded border border-dc-point/40 text-dc-point">현재 채택</span>}
                <span className="text-dc-ink3"> — {p.desc}</span>
              </div>
            ))}
            <p className="text-[11.5px] text-dc-ink3/70 pt-1 border-t border-dc-hair">
              🎫 옛 티켓 기능(중단됨) 갈래: <b>A</b>=본판(힘이 강할 때 1분 확인 후 발행) · <b>B</b>=선제 그림자(15초 확인 — "기다리면 늦는다" 검증) · <b>H</b>=같은 신호를 하이닉스 단일종목 탈것으로(병행 채점).
            </p>
          </div>
        )}
      </div>

      {/* 🌱 자가발전 확산 현황(RULES #10) — 단타 밖 영역들의 병행 실험 */}
      {data?.evolution && (() => {
        const ev = data.evolution!;
        const fa = ev.forecast_ab;
        return (
          <div className="rounded-xl border border-dc-hair bg-dc-bg1/60 px-3 py-2.5 space-y-1.5 text-[11.5px]">
            <p className="text-[13px] text-dc-ink2 font-medium">🌱 다른 영역의 자가발전 <span className="text-dc-ink3/70 font-normal">— 매일 밤 자동 기록·채점, 판정은 토요일 주간 점검에서</span></p>
            {/* 🪦 '시장국면 3파전'은 2026-07-25 실험 종료(세 판정이 내내 붙어 진전 0 · 250일 소급으로 이미 답이 나옴) */}
            <p className="text-dc-ink2">
              <b className="text-dc-ink2">아침 예보 A/B</b> — {fa && (fa.a.n > 0 || fa.b.n > 0)
                ? <>풀 재료 A {fa.a.hit}/{fa.a.n} vs 숫자만 B {fa.b.hit}/{fa.b.n} <span className="text-dc-ink3/70">(뉴스가 돕나 홀리나 — 30일 뒤 판정)</span></>
                : <span className="text-dc-ink3/70">B안 첫 발행은 내일 08시 — 표본 쌓는 중</span>}
            </p>
            <p className="text-dc-ink2">
              <b className="text-dc-ink2">섹터 전이 채점</b> — {ev.transfer && ev.transfer.length > 0
                ? ev.transfer.slice(0, 5).map((t) => `${t.etf} ${t.hit_rate}%(${t.n})`).join(' · ')
                : <span className="text-dc-ink3/70">오늘 16:05 첫 채점 — 연결선별 적중률이 쌓이면 레이더에 배지로 표시</span>}
            </p>
            <p className="text-dc-ink2">
              <b className="text-dc-ink2">AI 확신도</b> — {ev.calibration && Object.keys(ev.calibration).length > 0
                ? Object.entries(ev.calibration).map(([k, v]) => `${k} ${v.hit_rate}%(${v.n})`).join(' · ')
                : <span className="text-dc-ink3/70">"강한 확신이 진짜 더 맞나" — 오늘 분석부터 기록 시작</span>}
            </p>
            <p className="text-dc-ink2">
              <b className="text-dc-ink2">🔮 닮은 과거 찾기</b> — {ev.dejavu && (ev.dejavu.n ?? 0) > 0
                ? <>강한 편향 예측 {ev.dejavu.n}회 중 <b className="text-dc-ink2">{Math.round((ev.dejavu.hit ?? 0) / (ev.dejavu.n ?? 1) * 100)}%</b> 적중 <span className="text-dc-ink3/70">(닮은 과거 검색 — 오늘 가동 시작)</span></>
                : <span className="text-dc-ink3/70">닮은 과거 검색 엔진 — 오늘부터 가동, 편향 60%+일 때만 예측 기록</span>}
            </p>
            <p className="text-dc-ink2">
              <b className="text-dc-ink2">점수 스냅샷</b> — {ev.snapshots ?? 0}일 축적 <span className="text-dc-ink3/70">(주말 백테스터가 점수 계산법 비교를 돌릴 원료)</span>
            </p>
          </div>
        );
      })()}

      {/* 연대기 — 날짜 카드(최신 위) */}
      {chron.length > 0 ? (
        <div className="space-y-2">
          {chron.map((c) => <DayCard key={c.day} c={c} names={names} />)}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-dc-line1 px-4 py-8 text-center">
          <p className="text-[13px] text-dc-ink2">아직 비교 기록이 없어요</p>
          <p className="text-[11.5px] text-dc-ink3/70 mt-1">오늘 밤 16:05 첫 비교가 끝나면 첫 카드가 생깁니다.</p>
        </div>
      )}

      {err && <p className="text-[11.5px] text-dc-warn">서버 연결이 잠시 불안정해요 — 새로고침 해주세요.</p>}

      <p className="text-[11.5px] text-dc-ink3/70 leading-snug border-t border-dc-hair pt-2">
        읽는 법: <b>기준선</b> = 아무 생각 없이 한 방향만 찍었을 때의 적중률 — 이걸 못 이기면 무의미.
        <b> 자동 교체 조건</b> = 표본 150+ · 기준선 +3%p · 지금 쓰는 방식 +1%p를 3일 연속(하루 반짝으론 안 바뀜).
        교체 후 2일 연속 지면 자동 복귀(롤백). 새 계산 방식 등록은 주간 점검에서 승인.
      </p>
    </div>
  );
}

export default function StatsPage() {
  const [showLegacy, setShowLegacy] = useState(false);   // ④ 옛 기능 기록 — 기본 닫힘(펼칠 때만 마운트=폴링 재개)

  return (
    <div className="flex-1 overflow-auto">
      {/* 🎨 규격 V2(08-23) — 1020px 한 칸 콘솔(캔버스 값). 섹션은 헤어라인으로만 나눈다. */}
      <div className="max-w-[1020px] p-4 sm:px-7 sm:py-6 space-y-5">
        {/* ①②③ — 승격 심사 · 오늘 · 복기(접힘) */}
        <AlertLivePanel />

        {/* ④ 💤 옛 기능 기록 — 맨 아래 접힘 */}
        <div className="border-t border-dc-hair">
          <button onClick={() => setShowLegacy((v) => !v)} className="w-full flex flex-wrap items-center gap-x-2 gap-y-1 py-3 text-left">
            <span className="text-[14px] font-bold text-dc-ink1">💤 옛 기계 기록</span>
            <span className="text-[11.5px] text-dc-ink3">전면 개편(07-20) 이전 기계들의 보관소 — 대부분 중단 상태</span>
            <span className="ml-auto text-[11.5px] text-dc-point">{showLegacy ? '▾ 접기' : '▸ 펼치기'}</span>
          </button>
          {showLegacy && <div className="pb-3"><LegacyRecords /></div>}
        </div>
      </div>
    </div>
  );
}
